import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { FinancialGoal } from "@rentablo/api/apiv1";
import { Card } from "@rentablo/components";
import { useGoals } from "../../hooks/useGoals";
import { formatAmount, formatSignedAmount } from "../../utils/goalFormat";
import { getGoalStatus } from "../../utils/goalStatus";
import { GoalIcon } from "../GoalIcon";
import { GoalStatusBadge } from "../GoalStatusBadge";
import { GoalSparkline } from "./GoalSparkline";

const Tile = ({
  onClick,
  children,
}: {
  onClick?: () => void;
  children: ReactNode;
}) => (
  <Card size="maxi" onClick={onClick}>
    <Box
      data-testid="goals-dashboard-tile"
      width="100%"
      height={192}
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      pt={3.25}
      px={4}
    >
      {children}
    </Box>
  </Card>
);

const GoalTile = ({ goal }: { goal: FinancialGoal }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deviation = goal.absoluteDeviation ?? 0;

  return (
    <Tile onClick={() => navigate(`/goals/${goal.id}`)}>
      <Box display="flex" flexDirection="column" gap={1}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={3}
            minWidth={0}
            color="text.secondary"
          >
            <GoalIcon icon={goal.icon} size={20} />
            <Typography variant="caption2" noWrap>
              {goal.name}
            </Typography>
          </Box>
          <GoalStatusBadge status={getGoalStatus(goal.percentDeviation)} />
        </Box>
        <Typography variant="h4" component="span">
          {formatAmount(goal.actualAssets)}
        </Typography>
        <Typography variant="caption2" color="text.secondary">
          {t("goals.dashboard.plan", {
            amount: formatAmount(goal.expectedAssets),
          })}{" "}
          <Box
            component="span"
            color={deviation < 0 ? "text.negative" : "text.positive"}
          >
            {formatSignedAmount(deviation)}
          </Box>
        </Typography>
      </Box>
      <Box mx={-4}>
        <GoalSparkline goal={goal} />
      </Box>
    </Tile>
  );
};

/** The dashboard goal (else the first one), or an invitation to set one */
export const GoalsDashboardTile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { goals, isPending } = useGoals();

  if (isPending) {
    return <Skeleton variant="rounded" height={192} />;
  }
  if (goals.length > 0) return <GoalTile goal={goals[0]} />;

  return (
    <Tile>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" alignItems="center" gap={3} color="text.secondary">
          <GoalIcon icon="bullseye" size={20} />
          <Typography variant="caption2">
            {t("goals.dashboard.emptyTitle")}
          </Typography>
        </Box>
        <Typography variant="body3">
          {t("goals.dashboard.emptyText")}
        </Typography>
      </Box>
      <Box pb={4}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate("/goals/new")}
        >
          {t("goals.empty.create")}
        </Button>
      </Box>
    </Tile>
  );
};
